import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export type ClientHealth = "ok" | "watch" | "risk";
export type Priority = "high" | "normal" | "low";
export type VisitStatus = "planned" | "done" | "cancelled" | "to_reschedule";
export type VisitType = "technical_audit" | "commercial" | "training" | "client_relation" | "urgent_issue" | "field_followup";
export type ContactType = "visit" | "call" | "email" | "whatsapp" | "other";
export type NoteType = "call" | "whatsapp" | "email" | "visit" | "meeting" | "followup" | "other";
export type ActionStatus = "todo" | "in_progress" | "done";
export type Frequency = "monthly" | "quarterly" | "biannual" | "annual";

export interface FollowupRow {
  establishment_id: string;
  manager_phone: string | null;
  has_whatsapp: boolean;
  last_contact_date: string | null;
  last_contact_type: ContactType | null;
  last_contact_note: string | null;
  dashboard_url: string | null;
  client_username: string | null;
  password_hint: string | null;
  client_health: ClientHealth | null;
  health_reason: string | null;
  recommended_frequency: Frequency | null;
  priority: Priority | null;
  health_updated_at: string | null;
  health_updated_by: string | null;
}

export interface VisitRow {
  id: string;
  establishment_id: string;
  visit_date: string;
  start_time: string | null;
  end_time: string | null;
  is_full_day: boolean;
  location: string | null;
  visit_type: VisitType;
  status: VisitStatus;
  summary: string | null;
  detailed_report: string | null;
  issues_detected: string | null;
  actions_to_do: string | null;
  needs_followup: boolean;
  next_action_at: string | null;
  next_action_status: ActionStatus | null;
  client_health: ClientHealth | null;
  health_reason: string | null;
  recommended_frequency: Frequency | null;
  priority: Priority;
  created_by: string | null;
  created_at: string;
}

export interface NoteRow {
  id: string;
  establishment_id: string;
  note_datetime: string;
  note_type: NoteType;
  title: string;
  content: string | null;
  next_action: string | null;
  next_action_date: string | null;
  created_by: string | null;
  created_at: string;
}

export interface ClientFollowupListItem {
  id: string;
  name: string;
  city: string | null;
  slug: string;
  is_active: boolean;
  plan_type: string | null;
  last_visit_date: string | null;
  next_action_at: string | null;
  client_health: ClientHealth | null;
  priority: Priority | null;
}

// LIST — aggregated view for the table
// Aggregates from 3 sources to reflect real follow-up activity:
// - establishment_visits (formal field visits)
// - establishment_notes (chronological notes: calls, WhatsApp, meetings, etc.)
// - establishment_followup.last_contact_date (manual contact date in detail page)
export function useClientFollowupList() {
  return useQuery({
    queryKey: ["client-followup-list"],
    queryFn: async (): Promise<ClientFollowupListItem[]> => {
      const { data: ests, error } = await supabase
        .from("establishments")
        .select("id, name, city, slug, is_active, plan_type")
        .order("name");
      if (error) throw error;

      const ids = (ests || []).map((e) => e.id);
      if (ids.length === 0) return [];

      const [visitsRes, notesRes, followupRes] = await Promise.all([
        supabase
          .from("establishment_visits")
          .select("establishment_id, visit_date, next_action_at, next_action_status, client_health, priority, status")
          .in("establishment_id", ids)
          .order("visit_date", { ascending: false }),
        supabase
          .from("establishment_notes")
          .select("establishment_id, note_datetime, note_type, next_action_date")
          .in("establishment_id", ids),
        supabase
          .from("establishment_followup")
          .select("establishment_id, last_contact_date")
          .in("establishment_id", ids),
      ]);

      const visits = visitsRes.data || [];
      const notes = notesRes.data || [];
      const followups = followupRes.data || [];

      // Re-fetch followup with diagnostic fields (source of truth for health/priority)
      const { data: followupsFull } = await supabase
        .from("establishment_followup")
        .select("establishment_id, client_health, priority")
        .in("establishment_id", ids);
      const followupFullMap = new Map((followupsFull || []).map((f: any) => [f.establishment_id, f]));

      const now = new Date();
      const nowIso = now.toISOString();
      const todayIso = now.toISOString().slice(0, 10); // YYYY-MM-DD

      // Note types that count as a "contact" for the "Dernier contact" column
      const contactNoteTypes = new Set(["visit", "call", "whatsapp", "meeting"]);

      type Agg = {
        last_contact_date: string | null;
        next_action_at: string | null;
        client_health: ClientHealth | null;
        priority: Priority | null;
        has_any_note: boolean;
      };

      const byEst = new Map<string, Agg>();
      const ensure = (id: string): Agg => {
        let cur = byEst.get(id);
        if (!cur) {
          cur = { last_contact_date: null, next_action_at: null, client_health: null, priority: null, has_any_note: false };
          byEst.set(id, cur);
        }
        return cur;
      };

      for (const v of visits) {
        const cur = ensure(v.establishment_id);
        if (v.status === "done") {
          if (!cur.last_contact_date || v.visit_date > cur.last_contact_date) {
            cur.last_contact_date = v.visit_date;
          }
        }
        if (v.next_action_at && v.next_action_at >= nowIso && v.next_action_status !== "done") {
          if (!cur.next_action_at || v.next_action_at < cur.next_action_at) {
            cur.next_action_at = v.next_action_at;
          }
        }
        if (cur.client_health === null && v.client_health) cur.client_health = v.client_health as ClientHealth;
        if (cur.priority === null && v.priority) cur.priority = v.priority as Priority;
      }

      for (const n of notes) {
        const cur = ensure(n.establishment_id);
        cur.has_any_note = true;
        if (contactNoteTypes.has(n.note_type)) {
          const noteDate = (n.note_datetime || "").slice(0, 10);
          if (noteDate && (!cur.last_contact_date || noteDate > cur.last_contact_date)) {
            cur.last_contact_date = noteDate;
          }
        }
        if (n.next_action_date && n.next_action_date >= todayIso) {
          const candidate = new Date(`${n.next_action_date}T00:00:00`).toISOString();
          if (!cur.next_action_at || candidate < cur.next_action_at) {
            cur.next_action_at = candidate;
          }
        }
      }

      for (const f of followups) {
        if (!f.last_contact_date) continue;
        const cur = ensure(f.establishment_id);
        if (!cur.last_contact_date || f.last_contact_date > cur.last_contact_date) {
          cur.last_contact_date = f.last_contact_date;
        }
      }

      return (ests || []).map((e) => {
        const agg = byEst.get(e.id);
        const fup = followupFullMap.get(e.id);
        // Priority: establishment_followup (source of truth) → last visit fallback → "to_evaluate" if notes → null
        const resolvedHealth = (fup?.client_health as ClientHealth | undefined)
          ?? agg?.client_health
          ?? (agg?.has_any_note ? ("to_evaluate" as any) : null);
        const resolvedPriority = (fup?.priority as Priority | undefined) ?? agg?.priority ?? null;
        return {
          ...e,
          last_visit_date: agg?.last_contact_date ?? null,
          next_action_at: agg?.next_action_at ?? null,
          client_health: resolvedHealth,
          priority: resolvedPriority,
        };
      });
    },
  });
}

// FOLLOWUP (single establishment row)
export function useEstablishmentFollowup(establishmentId: string | undefined) {
  const qc = useQueryClient();

  const query = useQuery({
    queryKey: ["establishment-followup", establishmentId],
    enabled: !!establishmentId,
    queryFn: async (): Promise<FollowupRow | null> => {
      const { data, error } = await supabase
        .from("establishment_followup")
        .select("*")
        .eq("establishment_id", establishmentId!)
        .maybeSingle();
      if (error) throw error;
      return data as FollowupRow | null;
    },
  });

  const upsert = useMutation({
    mutationFn: async (payload: Partial<FollowupRow>) => {
      const { error } = await supabase
        .from("establishment_followup")
        .upsert({ establishment_id: establishmentId!, ...payload }, { onConflict: "establishment_id" });
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success("Suivi enregistré");
      qc.invalidateQueries({ queryKey: ["establishment-followup", establishmentId] });
      qc.invalidateQueries({ queryKey: ["client-followup-list"] });
    },
    onError: (e: any) => toast.error(e.message || "Erreur"),
  });

  return { ...query, upsert };
}

// VISITS
export function useEstablishmentVisits(establishmentId: string | undefined) {
  const qc = useQueryClient();

  const query = useQuery({
    queryKey: ["establishment-visits", establishmentId],
    enabled: !!establishmentId,
    queryFn: async (): Promise<VisitRow[]> => {
      const { data, error } = await supabase
        .from("establishment_visits")
        .select("*")
        .eq("establishment_id", establishmentId!)
        .order("visit_date", { ascending: false });
      if (error) throw error;
      return (data || []) as VisitRow[];
    },
  });

  const save = useMutation({
    mutationFn: async (payload: Partial<VisitRow> & { id?: string }) => {
      if (payload.id) {
        const { id, ...rest } = payload;
        const { error } = await supabase.from("establishment_visits").update(rest).eq("id", id);
        if (error) throw error;
      } else {
        const { data: u } = await supabase.auth.getUser();
        const { id: _omit, ...rest } = payload;
        const insertRow: any = {
          ...rest,
          establishment_id: establishmentId!,
          visit_date: payload.visit_date!,
          visit_type: payload.visit_type || "commercial",
          status: payload.status || "planned",
          priority: payload.priority || "normal",
          created_by: u.user?.id ?? null,
        };
        // Strip undefined keys so they don't override DB defaults / CHECK constraints
        Object.keys(insertRow).forEach((k) => insertRow[k] === undefined && delete insertRow[k]);
        const { error } = await supabase.from("establishment_visits").insert(insertRow);
        if (error) throw error;
      }
    },
    onSuccess: () => {
      toast.success("Visite enregistrée");
      qc.invalidateQueries({ queryKey: ["establishment-visits", establishmentId] });
      qc.invalidateQueries({ queryKey: ["client-followup-list"] });
    },
    onError: (e: any) => toast.error(e.message || "Erreur"),
  });

  const remove = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("establishment_visits").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success("Visite supprimée");
      qc.invalidateQueries({ queryKey: ["establishment-visits", establishmentId] });
      qc.invalidateQueries({ queryKey: ["client-followup-list"] });
    },
    onError: (e: any) => toast.error(e.message || "Erreur"),
  });

  return { ...query, save, remove };
}

// NOTES
export function useEstablishmentNotes(establishmentId: string | undefined) {
  const qc = useQueryClient();

  const query = useQuery({
    queryKey: ["establishment-notes", establishmentId],
    enabled: !!establishmentId,
    queryFn: async (): Promise<NoteRow[]> => {
      const { data, error } = await supabase
        .from("establishment_notes")
        .select("*")
        .eq("establishment_id", establishmentId!)
        .order("note_datetime", { ascending: false });
      if (error) throw error;
      return (data || []) as NoteRow[];
    },
  });

  const save = useMutation({
    mutationFn: async (payload: Partial<NoteRow> & { id?: string }) => {
      if (payload.id) {
        const { id, ...rest } = payload;
        const { error } = await supabase.from("establishment_notes").update(rest).eq("id", id);
        if (error) throw error;
      } else {
        const { data: u } = await supabase.auth.getUser();
        const { error } = await supabase.from("establishment_notes").insert({
          establishment_id: establishmentId!,
          note_datetime: payload.note_datetime || new Date().toISOString(),
          note_type: payload.note_type || "other",
          title: payload.title!,
          content: payload.content ?? null,
          next_action: payload.next_action ?? null,
          next_action_date: payload.next_action_date ?? null,
          created_by: u.user?.id ?? null,
        } as any);
        if (error) throw error;
      }
    },
    onSuccess: () => {
      toast.success("Note enregistrée");
      qc.invalidateQueries({ queryKey: ["establishment-notes", establishmentId] });
    },
    onError: (e: any) => toast.error(e.message || "Erreur"),
  });

  const remove = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("establishment_notes").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success("Note supprimée");
      qc.invalidateQueries({ queryKey: ["establishment-notes", establishmentId] });
    },
    onError: (e: any) => toast.error(e.message || "Erreur"),
  });

  return { ...query, save, remove };
}

// Manager email (for Contact section, read-only)
export function useEstablishmentManagerEmail(establishmentId: string | undefined) {
  return useQuery({
    queryKey: ["establishment-manager-email", establishmentId],
    enabled: !!establishmentId,
    queryFn: async (): Promise<string | null> => {
      const { data, error } = await supabase.functions.invoke("manage-users", {
        body: { action: "list", establishmentId },
      });
      if (error) return null;
      const users = (data?.users || []) as Array<{ email: string; role: string; establishment_id: string | null }>;
      // Super-admin receives ALL platform users → must filter client-side by establishment_id
      const scoped = users.filter((u) => u.establishment_id === establishmentId);
      const admin = scoped.find((u) => u.role === "admin");
      return admin?.email ?? scoped[0]?.email ?? null;
    },
  });
}
